import { useInfiniteQuery } from '@tanstack/react-query';
import { Account } from 'model/IChat';
import { localSearchRequest } from 'modules/locals/clients/host-search';
import { FilterObject } from 'modules/locals/utils/LocalSearchUrlParser';

import { FilterAggregates } from '../components/LocalSearchFilters/filterAggregatesTypes';

export type LocalSearchContent = {
  headerDescription: string;
  headerDescriptionKeys: string[];
  headerImage: string;
  headerImageKeys: string[];
  headerTitle: string;
  headerTitleKeys: string[];
  searchResultsTitle: string;
  searchResultsTitleKeys: string[];
  pageTitle: string;
  pageTitleKeys: string[];
  description: string;
  descriptionKeys: string[];
};

export type LocalSearchData = {
  hosts: any[];
  nrOfHosts: number;
  page: number;
  hasNextPage: boolean;
  aggregates: FilterAggregates;
  loading: boolean;
  fetchNextPage: () => void;
  content: LocalSearchContent;
  contentSeo: LocalSearchContent;
  error: any;
  filterParams?: string;
};

const INITIAL_CONTENT: LocalSearchContent = {
  headerDescription: null,
  headerDescriptionKeys: [],
  headerImage: null,
  headerImageKeys: [],
  headerTitle: null,
  headerTitleKeys: [],
  searchResultsTitle: null,
  searchResultsTitleKeys: [],
  pageTitle: null,
  pageTitleKeys: [],
  description: null,
  descriptionKeys: [],
};

type LocalSearchResponseType = {
  content: any;
  contentSeo: any;
  page: number;
  hosts: Account[];
  nrOfHosts: number;
  aggregates: any;
  pages: any[];
  pageParams: any[];
};

const getQueryKey = (filterObject: FilterObject) => {
  return ['local-search', { ...filterObject }].filter((f) => !!f);
};

const useLocalSearchData = (filterObject: FilterObject): LocalSearchData => {
  const queryKey = getQueryKey(filterObject);
  const {
    data,
    isPlaceholderData,
    isLoading,
    fetchNextPage,
    error,
    isFetching,
    hasNextPage,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => localSearchRequest(filterObject, pageParam),
    {
      getNextPageParam: (lastPage: any) => {
        return lastPage?.nextCursor ?? undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: filterObject.country !== undefined,
      keepPreviousData: true,
      placeholderData: () => {
        return {
          content: INITIAL_CONTENT,
          contentSeo: INITIAL_CONTENT,
          page: 1,
          hosts: [],
          nrOfHosts: 0,
          aggregates: null,
          filterParams: '',
          pages: [],
          pageParams: [undefined],
        };
      },
      select: (data): LocalSearchResponseType => {
        return {
          content: data?.pages[0]?.data?.content ?? INITIAL_CONTENT,
          contentSeo: data?.pages[0]?.data?.contentSeo ?? INITIAL_CONTENT,
          nrOfHosts: data?.pages?.[0]?.data?.results?.hits?.total ?? 0,
          aggregates: data?.pages?.[0]?.data?.results?.aggregations ?? null,
          page: data?.pages?.length ?? 1,
          hosts:
            data?.pages?.flatMap(
              (page) => page?.data?.results?.hits?.hits ?? [],
            ) || [],
          pages: data.pages,
          pageParams: data.pageParams,
        };
      },
    },
  );

  const nonDebouncedLoading = isLoading || isFetching;

  return {
    ...(data as LocalSearchResponseType),
    hasNextPage,
    error: error,
    fetchNextPage,
    loading: nonDebouncedLoading && !isPlaceholderData,
  };
};

export default useLocalSearchData;
